import {
  computed,
  ref,
  toValue,
  watch,
  type ComputedRef,
  type MaybeRefOrGetter,
  type Ref,
} from 'vue'

import type { ReportFilter } from '../models/filter.types'
import type {
  ReportSectionTabConfig,
  SectionLevelId,
  SectionTopTab,
} from '../models/layoutConfig.types'
import { SECTION_LEVELS } from '../models/layoutConfig.types'
import { useCustomizeStore } from '../stores/customizeStore'

// The section selection cache extracted from OrphexReportConfig.vue (v3 :299-330, :435-482,
// :503-570 — one of the three separable concerns the 656-line orchestrator carried). Selections
// survive a remount via customizeStore, which is what replaces v3's `_reqResetKey` remount
// counter: the workspace-keyed route wrapper remounts the page, and this cache restores the
// user's tab/topTab/group/level/dropdown choices.

const SELECTIONS_KIND = 'sectionSelectionsV1'

interface SectionSelectionsV1 {
  version: 1
  tab?: string
  topTab?: string
  /** Custom entity-group name; absent means "Overall". */
  group?: string
  level?: SectionLevelId
  /** tab name → dropdown index → selected OPTION index (stable across value edits). */
  dropdowns?: Record<string, Record<number, number>>
}

function parseSelections(value: unknown): SectionSelectionsV1 | null {
  if (typeof value !== 'object' || value === null) return null
  const candidate = value as Record<string, unknown>
  if (candidate.version !== 1) return null
  return candidate as unknown as SectionSelectionsV1
}

export interface UseSectionSelectionsOptions {
  sectionId: MaybeRefOrGetter<string>
  tabs: MaybeRefOrGetter<readonly ReportSectionTabConfig[]>
  topTabs?: MaybeRefOrGetter<readonly SectionTopTab[] | undefined>
  defaultLevel?: MaybeRefOrGetter<SectionLevelId | undefined>
}

export interface UseSectionSelections {
  /** The selected inner tab (validated against the resolved tab list; null only when empty). */
  selectedTab: ComputedRef<ReportSectionTabConfig | null>
  selectedTabName: Ref<string | null>
  selectedTopTab: ComputedRef<SectionTopTab | null>
  selectedTopTabName: Ref<string | null>
  /** Custom entity-group name, or null for "Overall". */
  group: Ref<string | null>
  level: Ref<SectionLevelId>
  /** Selected option index per dropdown for one tab (default: first option, v3 :471-480). */
  dropdownIndex: (tabName: string, dropdown: number) => number
  setDropdownIndex: (tabName: string, dropdown: number, option: number) => void
  /** The applied dropdown selections of one tab, shaped as report filters (v3 :421-433). */
  dropdownFilters: (tab: ReportSectionTabConfig) => ReportFilter[]
}

export function useSectionSelections(options: UseSectionSelectionsOptions): UseSectionSelections {
  const cache = useCustomizeStore()

  const selectedTabName = ref<string | null>(null)
  const selectedTopTabName = ref<string | null>(null)
  const group = ref<string | null>(null)
  const level = ref<SectionLevelId>(toValue(options.defaultLevel) ?? 'ad')
  const dropdowns = ref<Record<string, Record<number, number>>>({})

  function hydrate(sectionId: string): void {
    const saved = parseSelections(cache.getCached<unknown>(sectionId, SELECTIONS_KIND))
    const tabs = toValue(options.tabs)
    const topTabs = toValue(options.topTabs) ?? []
    const savedTab = saved?.tab != null ? tabs.find((tab) => tab.name === saved.tab) : undefined
    selectedTabName.value = (savedTab ?? tabs[0])?.name ?? null
    const savedTop =
      saved?.topTab != null ? topTabs.find((tab) => tab.name === saved.topTab) : undefined
    selectedTopTabName.value = (savedTop ?? topTabs[0])?.name ?? null
    group.value = typeof saved?.group === 'string' ? saved.group : null
    level.value =
      saved?.level && (SECTION_LEVELS as readonly string[]).includes(saved.level)
        ? saved.level
        : (toValue(options.defaultLevel) ?? 'ad')
    dropdowns.value = saved?.dropdowns ?? {}
  }

  // Hydrate per section id; a remount of the same section restores the persisted choices.
  watch(() => toValue(options.sectionId), hydrate, { immediate: true })

  // The resolved tab list can shrink when schedules re-hydrate — re-validate (v3 :484-493).
  watch(
    () => toValue(options.tabs),
    (tabs) => {
      if (!tabs.some((tab) => tab.name === selectedTabName.value)) {
        selectedTabName.value = tabs[0]?.name ?? null
      }
    },
  )

  watch(
    [selectedTabName, selectedTopTabName, group, level, dropdowns],
    ([tab, topTab, groupName, levelId, dropdownState]) => {
      cache.setCached<SectionSelectionsV1>(toValue(options.sectionId), SELECTIONS_KIND, {
        version: 1,
        ...(tab != null ? { tab } : {}),
        ...(topTab != null ? { topTab } : {}),
        ...(groupName != null ? { group: groupName } : {}),
        level: levelId,
        dropdowns: dropdownState,
      })
    },
    { deep: true },
  )

  const selectedTab = computed(
    () => toValue(options.tabs).find((tab) => tab.name === selectedTabName.value) ?? null,
  )
  const selectedTopTab = computed(
    () =>
      (toValue(options.topTabs) ?? []).find((tab) => tab.name === selectedTopTabName.value) ?? null,
  )

  function dropdownIndex(tabName: string, dropdown: number): number {
    return dropdowns.value[tabName]?.[dropdown] ?? 0
  }

  function setDropdownIndex(tabName: string, dropdown: number, option: number): void {
    dropdowns.value = {
      ...dropdowns.value,
      [tabName]: { ...(dropdowns.value[tabName] ?? {}), [dropdown]: option },
    }
  }

  function dropdownFilters(tab: ReportSectionTabConfig): ReportFilter[] {
    if (!tab.dropdowns?.length) return []
    return tab.dropdowns.flatMap((dropdown, index) => {
      const option = dropdown.options[dropdownIndex(tab.name, index)]
      if (!option || option.value === undefined) return []
      return [
        {
          field: dropdown.filter.field,
          match_mode: dropdown.filter.matchMode,
          value: option.value,
        },
      ]
    })
  }

  return {
    selectedTab,
    selectedTabName,
    selectedTopTab,
    selectedTopTabName,
    group,
    level,
    dropdownIndex,
    setDropdownIndex,
    dropdownFilters,
  }
}
